import { Button, ErrorPage, Modal, ModalContent } from '@clearcover/atlas';
import {
  CalculatePaymentPlanRequest,
  CalculatePaymentPlanResponse,
  GetPaymentsSummaryByTermNumberResponse,
  GetPolicyResponseTypeV2,
  UpdatePaymentPlanResponse,
} from '@clearcover/core-app-types';
import classnames from 'classnames';
import { Form, Formik, FormikHelpers } from 'formik';
import {
  PolicyDraftHeaderBar,
  StaggeredAnimatedComponent,
} from 'Policies/components';
import {
  PaymentForm,
  PaymentFrequencyChange,
  PaymentSchedule,
} from 'Policies/components/PaymentPlanSummary';
import { PolicyDetailsHeader } from 'Policies/components/PolicyDetailsHeader';
import { PolicyContext } from 'Policies/utils';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { RouteComponentProps, StaticContext } from 'react-router';
import {
  Column,
  ConfirmationModal,
  LoadingPage,
  useToast,
} from 'Shared/components';
import { useLazyApi } from 'Shared/services/Api';
import { useLazyApiV2, useNewApiV2 } from 'Shared/services/Api/makeApiHttp';
import { combineStrings } from 'Shared/utils';
import styles from './styles.module.css';
import { DriverOptionsResponse } from 'Policies/components/PolicyDriversSection/DriverForm';

export type PaymentsFormValueType = {
  paymentMethodId: string;
  paymentAmount: number;
};

export const PolicyPayPlanSummary: FC<RouteComponentProps<
  {
    id: string;
    draftId?: string;
  },
  StaticContext
>> = ({ match, history, location }) => {
  // Error modal state
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [
    driverOptions,
    setDriverOptions,
  ] = useState<DriverOptionsResponse | null>(null);

  const { toast } = useToast();

  const policyId = match.params.id;
  const draftId = match.params.draftId;

  // Parse query parameters
  const queryParams = new URLSearchParams(location.search);
  const installmentInterval = queryParams.get('installmentInterval');
  const daysOfWeek = queryParams.get('daysOfWeek');
  const daysOfMonth = queryParams.get('daysOfMonth');
  const feeStrategy = queryParams.get('feeStrategy');
  const isRenewalPlanChange = !!draftId;

  const [
    paymentScheduleData,
    setPaymentScheduleData,
  ] = useState<CalculatePaymentPlanResponse | null>(null);

  const [
    { data: policy, loading: policyLoading, error: policyError },
  ] = useNewApiV2<GetPolicyResponseTypeV2>(
    { url: `/policies/${policyId}` },
    false,
    false
  );

  const [
    {
      loading: billingLoading,
      data: billingInfoResponse,
      error: billingError,
      refetch: refetchBillingData,
    },
  ] = useNewApiV2<GetPaymentsSummaryByTermNumberResponse>(
    {
      url: `/payments/${policyId}/summary`,
    },
    false,
    false
  );

  const [
    { loading: calculatingPayPlan, error: calculatingPayPlanError },
    calculatePaymentPlan,
  ] = useLazyApi<CalculatePaymentPlanResponse>({
    url: isRenewalPlanChange
      ? `policies/renewal/${policyId}/calculatePayPlan`
      : 'policies/calculatePayPlan',
    method: 'POST',
  });

  const [{ loading: updatingPaymentPlanV1 }, updatePaymentPlanV1] = useLazyApi<
    UpdatePaymentPlanResponse
  >({
    url: 'policies/updatePayPlan',
    method: 'POST',
  });
  const [
    { loading: updatingPaymentPlanV2 },
    updatePaymentPlanV2,
  ] = useLazyApiV2<UpdatePaymentPlanResponse>({
    url: `policies/renewal/${policyId}/updatePayPlan`,
    method: 'POST',
  });
  const updatingPaymentPlan = updatingPaymentPlanV1 || updatingPaymentPlanV2;
  const updatePaymentPlan = isRenewalPlanChange
    ? updatePaymentPlanV2
    : updatePaymentPlanV1;

  // Execute calculatePaymentPlan when page loads with query params
  useEffect(() => {
    if (
      installmentInterval &&
      (installmentInterval === 'FULL_PAY' || daysOfWeek || daysOfMonth) &&
      policy?.policyDetails.policyId
    ) {
      const data: CalculatePaymentPlanRequest = {
        policyId: policy?.policyDetails.policyId,
        installmentInterval,
        ...(daysOfWeek && { daysOfWeek: daysOfWeek.split(',').map(String) }),
        ...(daysOfMonth && { daysOfMonth: daysOfMonth.split(',').map(Number) }),
        ...(feeStrategy && { feeStrategy }),
        ...(isRenewalPlanChange && {
          minimumRequiredDownpayment: { amount: 1, type: 'INSTALLMENT' },
        }),
      };

      calculatePaymentPlan({ data })
        .then(response => {
          if (
            response.data &&
            (response.data as CalculatePaymentPlanResponse)
          ) {
            setPaymentScheduleData(
              response.data as CalculatePaymentPlanResponse
            );
          }
        })
        .catch(error => {
          console.error('Error calculating payment plan:', error);
        });
    }
  }, [
    policy?.policyDetails.policyId,
    installmentInterval,
    daysOfWeek,
    daysOfMonth,
    calculatePaymentPlan,
    isRenewalPlanChange,
    feeStrategy,
  ]);

  const redirectUrl = isRenewalPlanChange
    ? `/policies/${policyId}/draft/${draftId}`
    : `/policies/${policyId}`;
  const redirectToPolicy = useCallback(
    (state?: any) => {
      history.push(redirectUrl, state);
    },
    [history, redirectUrl]
  );

  const proceedWithRedirect = useCallback(() => {
    const redirectData: {
      updatePolicySuccess: boolean;
      effectiveDate?: string;
    } = {
      updatePolicySuccess: true,
      effectiveDate: new Date().toISOString(),
    };

    redirectToPolicy(redirectData);
  }, [redirectToPolicy]);

  const handleExitWithoutChanges = useCallback(() => {
    history.push(redirectUrl);
  }, [history, redirectUrl]);

  const handleFinalizeChanges = useCallback(
    (
      _values: PaymentsFormValueType, // TODO: remove underscore and use values when we can send paymentMethodId
      { setStatus, setSubmitting }: FormikHelpers<PaymentsFormValueType>
    ) => {
      return updatePaymentPlan({
        data: {
          policyId: policy?.policyDetails.policyId,
          installmentInterval,
          // paymentMethodId: values.paymentMethodId, // TO BE DETERMINED
          // paymentTotal: values.paymentAmount, // TO BE DETERMINED
          ...(daysOfWeek && {
            daysOfWeek: daysOfWeek.split(',').map(String),
          }),
          ...(daysOfMonth && {
            daysOfMonth: daysOfMonth.split(',').map(Number),
          }),
          ...(isRenewalPlanChange && {
            minimumRequiredDownpayment: { amount: 1, type: 'INSTALLMENT' },
          }),
          ...(feeStrategy && { feeStrategy }),
        },
      })
        .then(() => {
          proceedWithRedirect();
        })
        .catch(err => {
          const errorMsg = err.response?.data?.message ?? err.message;
          const errorType = err.response?.data?.type;

          if (errorType === 'PaymentsError') {
            // Show modal for payment errors
            setErrorMessage(errorMsg);
            setErrorModalOpen(true);
          } else {
            // Show toast for other error types
            toast({
              message: errorMsg,
              type: 'error',
            });
          }

          setSubmitting(false);
          setStatus(errorMsg);
        });
    },
    [
      updatePaymentPlan,
      policy?.policyDetails.policyId,
      installmentInterval,
      daysOfWeek,
      daysOfMonth,
      isRenewalPlanChange,
      feeStrategy,
      proceedWithRedirect,
      toast,
    ]
  );

  const fetchBillingData = async () => {
    await refetchBillingData();
  };

  if (policyLoading || billingLoading) {
    return <LoadingPage />;
  }

  if (policyError || billingError || calculatingPayPlanError) {
    return (
      <ErrorPage
        key="error"
        variant="normal"
        iconType="info"
        errorMessage={`${policyError?.message}`}
      >
        <div className={styles.errorMessageContainer}>
          <h1>We're Sorry</h1>
          <div className={styles.errorMessage}>
            <span>
              We're unable to locate that policy. Please check the policy id and
            </span>
            <span>
              retry or contact your administrator if you believe this is an
              error
            </span>
          </div>
          <Button
            onClick={() => history.push('/policies')}
            block
            data-testid="go-to-policy-list"
            variant="secondary"
            size={'sm'}
          >
            View Policies
          </Button>
        </div>
      </ErrorPage>
    );
  }

  const hasProcessOnePaymentMethod = billingInfoResponse?.billingInfo?.paymentMethods.some(
    method => method.paymentProcessor === 'PROCESS_ONE'
  );
  const applicantName = combineStrings([
    policy?.policyHolder.name.firstName,
    policy?.policyHolder.name.lastName,
  ]);

  const timeZone = policy?.policyDetails.policyTimeZone || 'America/Chicago';

  return (
    <PolicyContext.Provider
      value={{
        details: {
          region: policy?.policyDetails.policyRegion || '',
          effectiveDate: policy?.policyDetails.effectivePeriod.startsAt || '',
          timeZone: timeZone,
          filingIdentifier: policy?.policyDetails.filingIdentifier || '',
          configDiscoveryParams:
            policy?.policyDetails.configDiscoveryParams || '',
          isPolicyDraft: false, // unused in this context
          isRenewalDraft: false, // unused in this context
          draftType: undefined, // unused in this context
          termId: policy?.policyDetails.termInfo.termId || '',
          mailingState: policy?.policyDetails.addresses.mailing.state || '',
          showDriverCfrs: policy?.policyDetails.showDriverCfrs || false,
          requiredAccessStatements: false, // unused in this context
          showMilitarySection:
            policy?.policyDetails.showMilitarySection ?? true,
          showStudentSection: policy?.policyDetails.showStudentSection ?? true,
          showEducation: policy?.policyDetails.showEducation ?? true,
          showOccupation: policy?.policyDetails.showOccupation ?? true,
          showRegisteredToApplicantOrSpouse:
            policy?.policyDetails.showRegisteredToApplicantOrSpouse ?? true,
          askForUserReportedLicenseStatus:
            policy?.policyDetails.askForUserReportedLicenseStatus ?? false,
          showExcludedDriverGender:
            policy?.policyDetails.showExcludedDriverGender ?? false,
          showExcludedDriverMaritalStatus:
            policy?.policyDetails.showExcludedDriverMaritalStatus ?? false,
          showPricingData: policy?.policyDetails?.showPricingData ?? true,
        },
        person: {
          name: applicantName,
        },
        flags: {
          renewalQuotesEnabled: false, // unused in this context
          mandatedDownPay: false,
          isFailingUnderwriting: false,
        },
        canEndorseDuringRegionalEvents: false, // unused in this context
        isEndorseablePolicy: false, // unused in this context
        systemOfRecord: 'clearcover', // unused in this context
        resourceEffectiveGrants: [], // unused in this context
        refetchHandler: () => {}, // unused in this context
        loading: policyLoading,
        productSegment: policy?.policyDetails.productSegment || 'standardAuto',
        draftList: [], // unused in this context
        removeDraft: () => {}, // unused in this context
        setDriverOptions,
        driverOptions,
      }}
    >
      <div className={styles.summaryContainer}>
        <PolicyDraftHeaderBar
          policyId={policy?.policyDetails.policyId}
          renewalDraftId={undefined}
          draftType={
            isRenewalPlanChange ? 'renewal_quote' : 'mid_term_plan_change'
          }
          updatedAt={undefined}
          timeZone={timeZone}
        />
        {installmentInterval && (
          <>
            <StaggeredAnimatedComponent>
              <PolicyDetailsHeader
                applicantName={applicantName}
                effectivePeriod={policy?.policyDetails.effectivePeriod}
                policyNumber={policy?.policyDetails.policyNumber}
                termInfo={policy?.policyDetails.termInfo}
                showTermChooser={false}
                draftList={[]}
                termStatusDetailsList={[]}
                processOne={hasProcessOnePaymentMethod}
                productSegment={policy?.policyDetails.productSegment}
                renewalDraftId={undefined}
                ignoreBanners={['pending_renewal']}
              />
            </StaggeredAnimatedComponent>

            <Column className={styles.summaryCol}>
              <StaggeredAnimatedComponent>
                <Column className={styles.summarySection}>
                  <div
                    className={classnames(styles.summaryHeader, styles.review)}
                  >
                    Review Policy Changes
                  </div>
                </Column>
              </StaggeredAnimatedComponent>

              <StaggeredAnimatedComponent>
                <Column className={styles.summarySection}>
                  <div
                    className={classnames(styles.summaryHeader2, styles.review)}
                  >
                    The customer has requested the following changes
                  </div>
                  <PaymentFrequencyChange
                    isLoading={billingLoading}
                    currentFrequency={{
                      installmentInterval:
                        billingInfoResponse?.billingInfo?.installmentInterval ||
                        '',
                      daysOfWeek:
                        billingInfoResponse?.billingInfo?.paymentsDueOn
                          ?.daysOfWeek,
                      daysOfMonth:
                        billingInfoResponse?.billingInfo?.paymentsDueOn
                          ?.daysOfMonth,
                    }}
                    newFrequency={{
                      installmentInterval: installmentInterval,
                      daysOfWeek: daysOfWeek
                        ? daysOfWeek.split(',')
                        : undefined,
                      daysOfMonth: daysOfMonth
                        ? daysOfMonth.split(',').map(day => parseInt(day))
                        : undefined,
                    }}
                  />
                </Column>
              </StaggeredAnimatedComponent>

              <StaggeredAnimatedComponent>
                <Column className={styles.summarySection}>
                  {calculatingPayPlan && <div>Calculating...</div>}
                  {!calculatingPayPlan && paymentScheduleData && (
                    <PaymentSchedule
                      paymentScheduleChanges={paymentScheduleData}
                      isRenewalPlanChange={isRenewalPlanChange}
                      totalAmount={
                        billingInfoResponse?.billingInfo?.totalTermCost || 0
                      }
                    />
                  )}
                  {!calculatingPayPlan && !paymentScheduleData && (
                    <div>No payment schedule data available</div>
                  )}
                </Column>
              </StaggeredAnimatedComponent>

              {billingInfoResponse?.billingInfo && (
                <Formik
                  initialValues={{
                    paymentMethodId:
                      billingInfoResponse.billingInfo.defaultPaymentMethod.id ||
                      '',
                    paymentAmount:
                      paymentScheduleData?.amountDueToday?.value || 0,
                  }}
                  onSubmit={handleFinalizeChanges}
                  enableReinitialize
                >
                  {({ values, setFieldValue, handleChange, isSubmitting }) => (
                    <Form>
                      <StaggeredAnimatedComponent>
                        <Column className={styles.summarySection}>
                          <PaymentForm
                            billingInfo={billingInfoResponse.billingInfo}
                            amountDueToday={
                              paymentScheduleData?.amountDueToday?.value || 0
                            }
                            fetchBillingData={fetchBillingData}
                            values={values}
                            setFieldValue={setFieldValue}
                            handleChange={handleChange}
                          />
                        </Column>
                      </StaggeredAnimatedComponent>

                      {/* Action Buttons */}
                      <div className={styles.buttonGroup}>
                        <Modal
                          variant="normal"
                          toggle={openModal => (
                            <Button
                              data-testid="pay-plan-summary-exit"
                              variant="danger-secondary"
                              type="button"
                              onClick={openModal}
                              disabled={false}
                            >
                              Exit without changes
                            </Button>
                          )}
                        >
                          {close => (
                            <ConfirmationModal
                              close={close}
                              onSubmit={handleExitWithoutChanges}
                              isSubmitting={false}
                              headerText="Exit without changes?"
                              bodyText="Any unsaved changes will be lost. Are you sure you want to exit?"
                              confirmText="Exit"
                              backText="Stay"
                              variant="primary"
                            />
                          )}
                        </Modal>

                        <Button
                          data-testid="pay-plan-summary-submit"
                          type="submit"
                          variant="primary"
                          className={styles.finalizeButton}
                          isSubmitting={updatingPaymentPlan || isSubmitting}
                        >
                          Finalize Policy Changes
                        </Button>
                      </div>
                    </Form>
                  )}
                </Formik>
              )}
            </Column>
          </>
        )}

        {/* Error Modal */}
        <Modal
          variant="normal"
          open={errorModalOpen}
          controlled={true}
          handleClose={() => {
            setErrorModalOpen(false);
          }}
        >
          {close => (
            <>
              <ModalContent>
                <div className={styles.errorModalContent}>
                  <h2 className={styles.errorModalHeader}>
                    We were unable to apply these changes due to an issue with
                    your payment.
                  </h2>
                  <div className={styles.errorModalBody}>
                    <p>
                      We encountered the following error while processing your
                      payment:
                    </p>
                    <div className={styles.errorMessageText}>
                      {errorMessage}
                    </div>
                    <p>Please select a new payment method and try again.</p>
                  </div>

                  <div className={styles.errorModalActions}>
                    <Button variant="primary" onClick={() => close()}>
                      Continue
                    </Button>
                  </div>
                </div>
              </ModalContent>
            </>
          )}
        </Modal>
      </div>
    </PolicyContext.Provider>
  );
};
